import { useEffect, type RefObject } from 'react';

/** Room left between the active tab and the row's edge, clear of the fade. */
const EDGE_ROOM = 24;

/**
 * Keeps the selected tab of a sideways-scrolling tab row on screen.
 *
 * On a phone a tab row is one line that scrolls, so a deep link (or a tab
 * opened from elsewhere) can select a tab that sits past the edge. When the
 * row overflows, this scrolls the row itself (never the page) just far enough
 * to show the tab marked `aria-selected="true"`. A row that fits, which is
 * every row on a desktop, is left exactly where it is.
 */
export function useActiveTabInView(listRef: RefObject<HTMLElement | null>, activeKey: string) {
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    const row = list.getBoundingClientRect();
    const tab = active.getBoundingClientRect();
    if (tab.left < row.left + EDGE_ROOM) {
      list.scrollLeft += tab.left - row.left - EDGE_ROOM;
    } else if (tab.right > row.right - EDGE_ROOM) {
      list.scrollLeft += tab.right - row.right + EDGE_ROOM;
    }
  }, [listRef, activeKey]);
}
